import { isDevelopment } from './env';
import * as Sentry from '@sentry/react';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogContext {
  [key: string]: unknown;
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  context?: LogContext;
  timestamp: string;
  component?: string;
  userId?: string;
}

class Logger {
  private isDevelopment = isDevelopment();

  private formatMessage(level: LogLevel, message: string, context?: LogContext, component?: string): string {
    const timestamp = new Date().toISOString();
    const prefix = component ? `[${component}]` : '';
    const contextStr = context ? ` | Context: ${JSON.stringify(context)}` : '';
    return `${timestamp} [${level.toUpperCase()}] ${prefix} ${message}${contextStr}`;
  }

  private shouldLog(level: LogLevel): boolean {
    // In production, only log warnings and errors
    if (!this.isDevelopment) {
      return level === 'warn' || level === 'error';
    }
    return true;
  }

  private logToConsole(level: LogLevel, formattedMessage: string, context?: LogContext): void {
    if (!this.shouldLog(level)) return;

    switch (level) {
      case 'debug':
        console.debug(formattedMessage, context || '');
        break;
      case 'info':
        console.info(formattedMessage, context || '');
        break;
      case 'warn':
        console.warn(formattedMessage, context || '');
        break;
      case 'error':
        console.error(formattedMessage, context || '');
        break;
    }
  }

  debug(message: string, context?: LogContext, component?: string): void {
    const formattedMessage = this.formatMessage('debug', message, context, component);
    this.logToConsole('debug', formattedMessage, context);
  }

  info(message: string, context?: LogContext, component?: string): void {
    const formattedMessage = this.formatMessage('info', message, context, component);
    this.logToConsole('info', formattedMessage, context);
  }

  warn(message: string, context?: LogContext, component?: string): void {
    const formattedMessage = this.formatMessage('warn', message, context, component);
    this.logToConsole('warn', formattedMessage, context);
    
    // Send critical warnings to Sentry in production
    if (!this.isDevelopment) {
      Sentry.captureMessage(message, {
        extra: context,
        tags: { component },
        level: 'warning'
      });
    }
  }

  error(message: string, error?: Error | unknown, context?: LogContext, component?: string): void {
    const errorContext: LogContext = {
      ...context,
    };

    if (error instanceof Error) {
      errorContext.errorName = error.name;
      errorContext.errorMessage = error.message;
      if (this.isDevelopment) {
        errorContext.errorStack = error.stack;
      }
    } else if (error && typeof error === 'object' && error !== null && 'message' in error) {
      errorContext.errorDetails = error;
    }

    const formattedMessage = this.formatMessage('error', message, errorContext, component);
    this.logToConsole('error', formattedMessage, errorContext);

    // In production, send errors to Sentry for monitoring
    if (!this.isDevelopment && error instanceof Error) {
      Sentry.captureException(error, { 
        extra: context, 
        tags: { component },
        level: 'error'
      });
    } else if (!this.isDevelopment && typeof error === 'object' && error !== null) {
      // Capture non-Error objects as messages with context
      Sentry.captureMessage(message, {
        extra: { error, ...context },
        tags: { component },
        level: 'error'
      });
    }
  }

  // Specialized logging methods for common use cases
  
  authError(message: string, error?: Error | unknown, context?: LogContext): void {
    this.error(message, error, context, 'Auth');
  }

  dbError(message: string, error?: Error | unknown, context?: LogContext): void {
    this.error(message, error, context, 'Database');
  }

  apiError(message: string, error?: Error | unknown, context?: LogContext): void {
    this.error(message, error, context, 'API');
  }

  uploadError(message: string, error?: Error | unknown, context?: LogContext): void {
    this.error(message, error, context, 'Upload');
  }

  formError(message: string, error?: Error | unknown, context?: LogContext): void {
    this.error(message, error, context, 'Form');
  }

  // Development-only logging
  devLog(message: string, context?: LogContext, component?: string): void {
    if (this.isDevelopment) {
      this.debug(`[DEV] ${message}`, context, component);
    }
  }

  // Performance logging
  performance(operation: string, duration: number, context?: LogContext, component?: string): void {
    this.info(`Performance: ${operation} completed in ${duration}ms`, context, component);
  }

  // User action logging (for analytics)
  userAction(action: string, context?: LogContext, component?: string): void {
    this.info(`User Action: ${action}`, context, component);
  }

  // Set user context for Sentry (in production)
  setUserContext(user: { id: string; email?: string; role?: string }): void {
    if (!this.isDevelopment) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.role
      });
    }
  }

  // Clear user context (on logout)
  clearUserContext(): void {
    if (!this.isDevelopment) {
      Sentry.setUser(null);
    }
  }
}

// Create singleton instance
export const logger = new Logger();

// Export commonly used methods for convenience with proper binding
export const debug = logger.debug.bind(logger);
export const info = logger.info.bind(logger);
export const warn = logger.warn.bind(logger);
export const error = logger.error.bind(logger);
export const authError = logger.authError.bind(logger);
export const dbError = logger.dbError.bind(logger);
export const apiError = logger.apiError.bind(logger);
export const uploadError = logger.uploadError.bind(logger);
export const formError = logger.formError.bind(logger);
export const devLog = logger.devLog.bind(logger);
export const setUserContext = logger.setUserContext.bind(logger);
export const clearUserContext = logger.clearUserContext.bind(logger); 